import type { JsonValue } from 'flags'

import { decodeCompanyRollout, type CompanyRollout } from './companyRollout'

/**
 * One flag, declared once, in the app's models.
 *
 * The server builds the Vercel Flags declaration from it, and the browser reads
 * the default from it. Nothing in here imports the SDK, so it is safe to bundle.
 */
export type FlagDefinition<Value extends JsonValue> = {
    /** The flag's key in Vercel Flags, in kebab-case. */
    key: string
    /** Shown in the Vercel dashboard and in the Flags Explorer. */
    description: string
    /**
     * Served when Vercel Flags cannot answer, or answers with a value of the
     * wrong shape. A new flag defaults to off. A flag that guards a live
     * feature defaults to its production value, so a missing config never
     * changes production. Tests start from it too.
     */
    defaultValue: Value
    /** The values the Flags Explorer offers as overrides. */
    options?: Value[]
    /** Narrows what Vercel Flags served. `undefined` means the value has the wrong shape. */
    decode: (value: unknown) => Value | undefined
}

export type FlagDefinitions = Record<string, FlagDefinition<JsonValue>>

export type FlagValues<Definitions extends FlagDefinitions> = {
    [Name in keyof Definitions]: Definitions[Name] extends FlagDefinition<infer Value> ? Value : never
}

type FlagDescription<Value> = {
    key: string
    description: string
    defaultValue: Value
}

export const booleanFlag = ({ key, description, defaultValue }: FlagDescription<boolean>): FlagDefinition<boolean> => ({
    key,
    description,
    defaultValue,
    options: [false, true],
    decode: (value) => (typeof value === 'boolean' ? value : undefined)
})

export const companyRolloutFlag = ({
    key,
    description,
    defaultValue
}: FlagDescription<CompanyRollout>): FlagDefinition<CompanyRollout> => ({
    key,
    description,
    defaultValue,
    decode: decodeCompanyRollout
})

const mapDefinitions = <Definitions extends FlagDefinitions>(
    definitions: Definitions,
    pick: (name: string, definition: FlagDefinition<JsonValue>) => JsonValue
): FlagValues<Definitions> => {
    const entries = Object.entries(definitions).map(([name, definition]) => [name, pick(name, definition)])

    // eslint-disable-next-line typescript/no-unsafe-type-assertion -- the entries are built from the same keys, so the record has every name of the map type
    return Object.fromEntries(entries) as FlagValues<Definitions>
}

export const defaultFlagValues = <Definitions extends FlagDefinitions>(definitions: Definitions) =>
    mapDefinitions(definitions, (_name, definition) => definition.defaultValue)

/**
 * Reads a record of served values against the definitions.
 *
 * A missing value takes its default. A value of the wrong shape takes its
 * default too, and is reported by key, so a typo in the dashboard shows in the
 * logs instead of turning a feature off without a trace.
 */
export const decodeFlagValues = <Definitions extends FlagDefinitions>(
    definitions: Definitions,
    served: unknown
): FlagValues<Definitions> => {
    const record = typeof served === 'object' && served !== null ? served : {}

    return mapDefinitions(definitions, (name, definition) => {
        const value: unknown = Reflect.get(record, name)
        if (value === undefined) return definition.defaultValue

        const decoded = definition.decode(value)
        if (decoded !== undefined) return decoded

        console.error(`flags: "${definition.key}" served a value of the wrong shape, falling back to its default`)
        return definition.defaultValue
    })
}
